import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// La Settimana Enigmistica — area "per i grandi"
// Enigmi classici in italiano, contenuti curati, tutto lato client.
// ---------------------------------------------------------------------------

const card = "bg-white rounded-2xl p-5 shadow-lg";

// Ricompensa (monete/XP) al completamento di un enigma, una sola volta per schema
function useSolvedReward(onSolved) {
  const [reward, setReward] = useState(null);
  const doneRef = useRef(false);
  const fire = async (game, extra = {}) => {
    if (doneRef.current || !onSolved) return;
    doneRef.current = true;
    const r = await onSolved(game, extra);
    if (r) setReward(r);
  };
  const reset = () => { doneRef.current = false; setReward(null); };
  return { reward, fire, reset };
}
function RewardLine({ reward }) {
  if (!reward) return null;
  return (
    <p className="text-amber-600 font-bold text-center mb-2" data-testid="enig-reward">
      +{reward.coins_earned} 💰 · +{reward.xp_earned} XP{reward.new_record ? " · 🏆 Record!" : ""}{reward.leveled_up ? ` · ⬆️ Livello ${reward.level_after}!` : ""}
    </p>
  );
}

// ===================== SUDOKU (generatore con difficoltà) =====================
function genSolvedSudoku() {
  const g = Array.from({ length: 9 }, () => Array(9).fill(0));
  const canPlace = (r, c, n) => {
    for (let i = 0; i < 9; i++) if (g[r][i] === n || g[i][c] === n) return false;
    const br = Math.floor(r / 3) * 3, bc = Math.floor(c / 3) * 3;
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) if (g[br + i][bc + j] === n) return false;
    return true;
  };
  const fill = (pos) => {
    if (pos === 81) return true;
    const r = Math.floor(pos / 9), c = pos % 9;
    const nums = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    for (let i = nums.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[nums[i], nums[j]] = [nums[j], nums[i]]; }
    for (const n of nums) {
      if (canPlace(r, c, n)) { g[r][c] = n; if (fill(pos + 1)) return true; g[r][c] = 0; }
    }
    return false;
  };
  fill(0);
  return g;
}

function makeSudoku(difficulty) {
  const sol = genSolvedSudoku();
  const clues = difficulty === "facile" ? 44 : difficulty === "difficile" ? 28 : 34;
  const puzzle = sol.map((row) => [...row]);
  let toRemove = 81 - clues;
  const cells = Array.from({ length: 81 }, (_, i) => i);
  for (let i = cells.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[cells[i], cells[j]] = [cells[j], cells[i]]; }
  for (const idx of cells) {
    if (toRemove <= 0) break;
    const r = Math.floor(idx / 9), c = idx % 9;
    if (puzzle[r][c] !== 0) { puzzle[r][c] = 0; toRemove--; }
  }
  return {
    puzzle: puzzle.flat().map(String).map((x) => (x === "0" ? "0" : x)).join(""),
    solution: sol.flat().join(""),
  };
}

function Sudoku({ onSolved }) {
  const [difficulty, setDifficulty] = useState("facile");
  const [p, setP] = useState(() => makeSudoku("facile"));
  const givens = useMemo(() => p.puzzle.split("").map((c) => c !== "0"), [p]);
  const [grid, setGrid] = useState(() => p.puzzle.split(""));
  const [sel, setSel] = useState(null);
  const [msg, setMsg] = useState("");
  const { reward, fire, reset } = useSolvedReward(onSolved);

  const newGame = (diff) => {
    const np = makeSudoku(diff);
    setP(np);
    setGrid(np.puzzle.split(""));
    setSel(null);
    setMsg("");
    reset();
  };

  const setVal = (v) => {
    if (sel === null || givens[sel]) return;
    const g = [...grid]; g[sel] = v; setGrid(g); setMsg("");
  };

  const check = () => {
    if (grid.join("") === p.solution) { setMsg("ok"); fire("sudoku", { difficulty }); }
    else if (grid.includes("0")) setMsg("incomplete");
    else setMsg("wrong");
  };

  return (
    <div className={card} data-testid="enig-sudoku">
      <h3 className="text-xl font-bold text-gray-800 mb-1">🔢 Sudoku</h3>
      <p className="text-sm text-gray-500 mb-3">Completa la griglia con i numeri 1-9, senza ripetizioni in righe, colonne e riquadri.</p>
      <div className="flex gap-2 mb-4">
        {["facile", "medio", "difficile"].map((d) => (
          <button key={d} onClick={() => { setDifficulty(d); newGame(d); }} data-testid={`sudoku-diff-${d}`}
            className={`flex-1 py-2 rounded-full text-sm font-bold capitalize ${difficulty === d ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600"}`}>{d}</button>
        ))}
      </div>
      <div className="grid grid-cols-9 w-full max-w-[360px] mx-auto border-2 border-gray-800 mb-4">
        {grid.map((c, i) => {
          const r = Math.floor(i / 9), col = i % 9;
          const isGiven = givens[i];
          return (
            <button key={`cell-${i}`} onClick={() => !isGiven && setSel(i)} data-testid={`sudoku-cell-${i}`}
              className={`aspect-square flex items-center justify-center text-sm md:text-base font-bold
                ${r % 3 === 0 ? "border-t-2 border-t-gray-800" : "border-t border-t-gray-300"}
                ${col % 3 === 0 ? "border-l-2 border-l-gray-800" : "border-l border-l-gray-300"}
                ${isGiven ? "bg-gray-100 text-gray-800" : "text-blue-600"}
                ${sel === i ? "bg-amber-200" : ""}`}>
              {c !== "0" ? c : ""}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-9 gap-1 max-w-[360px] mx-auto mb-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <button key={n} onClick={() => setVal(String(n))} data-testid={`sudoku-num-${n}`}
            className="py-2 rounded-lg bg-indigo-100 text-indigo-700 font-bold hover:bg-indigo-200">{n}</button>
        ))}
      </div>
      <button onClick={() => setVal("0")} className="text-sm text-gray-500 underline mb-4" data-testid="sudoku-erase">Cancella cella</button>
      {msg === "ok" && <p className="text-green-600 font-bold mb-2" data-testid="sudoku-result">🎉 Perfetto! Sudoku risolto!</p>}
      <RewardLine reward={reward} />
      {msg === "wrong" && <p className="text-red-600 font-bold mb-2" data-testid="sudoku-result">Qualche numero non è corretto, riprova.</p>}
      {msg === "incomplete" && <p className="text-amber-600 font-bold mb-2" data-testid="sudoku-result">Griglia incompleta.</p>}
      <div className="flex gap-3">
        <button onClick={check} className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl font-bold hover:opacity-90" data-testid="sudoku-check">Verifica</button>
        <button onClick={() => newGame(difficulty)} className="flex-1 bg-gray-200 text-gray-700 py-2.5 rounded-xl font-bold hover:bg-gray-300" data-testid="sudoku-new">Nuovo schema</button>
      </div>
    </div>
  );
}

// ===================== CRUCIPUZZLE (word search) =====================
const CP_WORDS = ["SOLE", "MARE", "GATTO", "LIBRO", "FIORE", "MUSICA", "LUNA", "STELLA", "PANE", "CANE"];
const CP_SIZE = 11;
const DIRS = [[0, 1], [1, 0], [1, 1], [-1, 1], [0, -1], [-1, 0], [-1, -1], [1, -1]];

function buildGrid(words) {
  const g = Array.from({ length: CP_SIZE }, () => Array(CP_SIZE).fill(""));
  const placed = {};
  for (const w of words) {
    let done = false;
    for (let attempt = 0; attempt < 200 && !done; attempt++) {
      const [dr, dc] = DIRS[Math.floor(Math.random() * DIRS.length)];
      const r0 = Math.floor(Math.random() * CP_SIZE);
      const c0 = Math.floor(Math.random() * CP_SIZE);
      const rEnd = r0 + dr * (w.length - 1);
      const cEnd = c0 + dc * (w.length - 1);
      if (rEnd < 0 || rEnd >= CP_SIZE || cEnd < 0 || cEnd >= CP_SIZE) continue;
      let ok = true;
      const cells = [];
      for (let i = 0; i < w.length; i++) {
        const r = r0 + dr * i, c = c0 + dc * i;
        if (g[r][c] && g[r][c] !== w[i]) { ok = false; break; }
        cells.push(`${r},${c}`);
      }
      if (!ok) continue;
      for (let i = 0; i < w.length; i++) g[r0 + dr * i][c0 + dc * i] = w[i];
      placed[w] = cells;
      done = true;
    }
  }
  const alph = "ABCDEFGHILMNOPQRSTUVZ";
  for (let r = 0; r < CP_SIZE; r++)
    for (let c = 0; c < CP_SIZE; c++)
      if (!g[r][c]) g[r][c] = alph[Math.floor(Math.random() * alph.length)];
  return { g, placed };
}

function Crucipuzzle({ words, onSolved }) {
  const list = words && words.length >= 4 ? words : CP_WORDS;
  const [{ g, placed }, setData] = useState(() => buildGrid(list));
  const [start, setStart] = useState(null);
  const [found, setFound] = useState({});

  const foundCells = useMemo(() => {
    const s = new Set();
    Object.entries(found).forEach(([w, ok]) => { if (ok) placed[w]?.forEach((c) => s.add(c)); });
    return s;
  }, [found, placed]);

  const lineCells = (a, b) => {
    const [r0, c0] = a.split(",").map(Number);
    const [r1, c1] = b.split(",").map(Number);
    const dr = Math.sign(r1 - r0), dc = Math.sign(c1 - c0);
    const len = Math.max(Math.abs(r1 - r0), Math.abs(c1 - c0)) + 1;
    if (r0 !== r1 && c0 !== c1 && Math.abs(r1 - r0) !== Math.abs(c1 - c0)) return null;
    const cells = [];
    for (let i = 0; i < len; i++) cells.push(`${r0 + dr * i},${c0 + dc * i}`);
    return cells;
  };

  const clickCell = (r, c) => {
    const key = `${r},${c}`;
    if (!start) { setStart(key); return; }
    const cells = lineCells(start, key);
    setStart(null);
    if (!cells) return;
    const fwd = cells.join("|");
    const rev = [...cells].reverse().join("|");
    for (const [w, path] of Object.entries(placed)) {
      const pj = path.join("|");
      if ((pj === fwd || pj === rev) && !found[w]) { setFound((f) => ({ ...f, [w]: true })); break; }
    }
  };

  const allFound = list.every((w) => found[w]);
  const { reward, fire, reset } = useSolvedReward(onSolved);
  useEffect(() => { if (allFound) fire("crucipuzzle"); }, [allFound]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={card} data-testid="enig-crucipuzzle">
      <h3 className="text-xl font-bold text-gray-800 mb-1">🔎 Crucipuzzle</h3>
      <p className="text-sm text-gray-500 mb-4">Trova le parole nascoste (orizzontali, verticali, diagonali). Tocca la prima e l'ultima lettera.</p>
      <div className="grid mx-auto mb-4" style={{ gridTemplateColumns: `repeat(${CP_SIZE}, minmax(0,1fr))`, maxWidth: 360 }}>
        {g.map((row, r) => row.map((ch, c) => {
          const key = `${r},${c}`;
          const isFound = foundCells.has(key);
          const isStart = start === key;
          return (
            <button key={key} onClick={() => clickCell(r, c)} data-testid={`cp-cell-${r}-${c}`}
              className={`aspect-square flex items-center justify-center text-[11px] md:text-xs font-bold rounded
                ${isFound ? "bg-green-400 text-white" : isStart ? "bg-amber-300" : "bg-gray-50 text-gray-700 hover:bg-amber-100"}`}>
              {ch}
            </button>
          );
        }))}
      </div>
      <div className="flex flex-wrap gap-2 justify-center">
        {list.map((w) => (
          <span key={w} data-testid={`cp-word-${w}`} className={`text-sm font-bold px-2 py-1 rounded-full ${found[w] ? "bg-green-100 text-green-700 line-through" : "bg-gray-100 text-gray-600"}`}>{w}</span>
        ))}
      </div>
      {allFound && <p className="text-green-600 font-bold text-center mt-3" data-testid="cp-complete">🎉 Trovate tutte!</p>}
      <RewardLine reward={reward} />
      <button onClick={() => { setData(buildGrid(list)); setFound({}); setStart(null); reset(); }} className="w-full mt-4 bg-gray-200 text-gray-700 py-2.5 rounded-xl font-bold hover:bg-gray-300" data-testid="cp-new">Nuovo schema</button>
    </div>
  );
}

// ===================== CRUCIVERBA (definizioni) =====================
const CRUCIVERBA = [
  { n: 1, clue: "Il nostro satellite naturale", answer: "LUNA" },
  { n: 2, clue: "La città eterna, capitale d'Italia", answer: "ROMA" },
  { n: 3, clue: "Astro che illumina il giorno", answer: "SOLE" },
  { n: 4, clue: "Grande distesa d'acqua salata", answer: "MARE" },
  { n: 5, clue: "Si legge, ha molte pagine", answer: "LIBRO" },
  { n: 6, clue: "Fedele amico dell'uomo che abbaia", answer: "CANE" },
];

function Cruciverba({ items, onSolved }) {
  const rows = useMemo(() => items.map((it, i) => ({ n: i + 1, clue: it.clue, answer: it.answer })), [items]);
  const [vals, setVals] = useState({});
  const [msg, setMsg] = useState("");
  const { reward, fire } = useSolvedReward(onSolved);

  const setChar = (ri, ci, v) => {
    setVals((prev) => ({ ...prev, [`${ri}-${ci}`]: v.toUpperCase().slice(-1) }));
    setMsg("");
  };

  const check = () => {
    const ok = rows.every((row) => row.answer.split("").every((ch, ci) => (vals[`${row.n}-${ci}`] || "") === ch));
    setMsg(ok ? "ok" : "wrong");
    if (ok) fire("cruciverba");
  };
  const reveal = () => {
    const v = {};
    rows.forEach((row) => row.answer.split("").forEach((ch, ci) => { v[`${row.n}-${ci}`] = ch; }));
    setVals(v);
    setMsg("ok");
  };

  return (
    <div className={card} data-testid="enig-cruciverba">
      <h3 className="text-xl font-bold text-gray-800 mb-1">✏️ Parole Crociate</h3>
      <p className="text-sm text-gray-500 mb-4">Leggi le definizioni e scrivi le parole, una lettera per casella.</p>
      <div className="space-y-3 mb-4">
        {rows.map((row) => (
          <div key={row.n} data-testid={`cw-row-${row.n}`}>
            <p className="text-sm text-gray-700 mb-1"><b>{row.n}.</b> {row.clue}</p>
            <div className="flex gap-1 flex-wrap">
              {row.answer.split("").map((_, ci) => (
                <input
                  key={`${row.n}-${ci}`}
                  maxLength={1}
                  value={vals[`${row.n}-${ci}`] || ""}
                  onChange={(e) => setChar(row.n, ci, e.target.value)}
                  data-testid={`cw-input-${row.n}-${ci}`}
                  className="w-9 h-9 text-center border-2 border-gray-300 rounded font-bold uppercase text-gray-800 focus:border-indigo-500 outline-none"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {msg === "ok" && <p className="text-green-600 font-bold mb-2" data-testid="cw-result">🎉 Complimenti, tutto corretto!</p>}
      <RewardLine reward={reward} />
      {msg === "wrong" && <p className="text-red-600 font-bold mb-2" data-testid="cw-result">Ci sono errori, continua a provare.</p>}
      <div className="flex gap-3">
        <button onClick={check} className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl font-bold hover:opacity-90" data-testid="cw-check">Verifica</button>
        <button onClick={reveal} className="flex-1 bg-gray-200 text-gray-700 py-2.5 rounded-xl font-bold hover:bg-gray-300" data-testid="cw-reveal">Soluzione</button>
      </div>
    </div>
  );
}

// ===================== REBUS & ANAGRAMMI =====================
const ANAGRAMMI = [
  { answer: "GIARDINO", hint: "C'è pieno di fiori e piante" },
  { answer: "FINESTRA", hint: "Si apre per far entrare l'aria" },
  { answer: "OROLOGIO", hint: "Segna le ore" },
  { answer: "MONTAGNA", hint: "Alta, spesso innevata" },
];
const REBUS = [
  { clue: "🐝 + O = ?", hint: "Un legume verde", answer: "BAO... (fava)", solution: "APE + O" },
  { clue: "P + 🐜 + ORE = ?", hint: "Chi lavora manualmente", answer: "PITTORE", solution: "P + formica(ant) ... indovina!" },
  { clue: "🌊 + RE = ?", hint: "Sovrano degli abissi", answer: "MARE + RE", solution: "MA + RE" },
];

function scramble(w) {
  const a = w.split("");
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[a[i], a[j]] = [a[j], a[i]]; }
  const s = a.join("");
  return s === w ? scramble(w) : s;
}

function RebusAnagrammi({ anagrammi, rebus, onSolved }) {
  const [tab, setTab] = useState("anagrammi");
  const scrambled = useMemo(() => anagrammi.map((a) => scramble(a.answer)), [anagrammi]);
  const [inputs, setInputs] = useState({});
  const [status, setStatus] = useState({});
  const [revealed, setRevealed] = useState({});

  const { reward, fire } = useSolvedReward(onSolved);
  const checkAna = (i) => {
    const ok = (inputs[i] || "").trim().toUpperCase() === anagrammi[i].answer;
    const next = { ...status, [i]: ok ? "ok" : "wrong" };
    setStatus(next);
    const solved = anagrammi.filter((_, j) => next[j] === "ok").length;
    if (solved === anagrammi.length) fire("anagrammi", { score: solved, total: anagrammi.length });
  };

  return (
    <div className={card} data-testid="enig-rebus">
      <h3 className="text-xl font-bold text-gray-800 mb-1">🧠 Rebus & Anagrammi</h3>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("anagrammi")} data-testid="rb-tab-anagrammi" className={`flex-1 py-2 rounded-full font-bold text-sm ${tab === "anagrammi" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600"}`}>Anagrammi</button>
        <button onClick={() => setTab("rebus")} data-testid="rb-tab-rebus" className={`flex-1 py-2 rounded-full font-bold text-sm ${tab === "rebus" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600"}`}>Rebus</button>
      </div>

      {tab === "anagrammi" && (
        <div className="space-y-4">
          <RewardLine reward={reward} />
          {anagrammi.map((a, i) => (
            <div key={a.answer} className="bg-gray-50 rounded-xl p-3" data-testid={`ana-${i}`}>
              <div className="text-2xl font-bold tracking-[0.3em] text-indigo-700 mb-1">{scrambled[i]}</div>
              <p className="text-xs text-gray-500 mb-2">Indizio: {a.hint} ({a.answer.length} lettere)</p>
              <div className="flex gap-2">
                <input value={inputs[i] || ""} onChange={(e) => setInputs((s) => ({ ...s, [i]: e.target.value }))}
                  data-testid={`ana-input-${i}`} placeholder="La tua risposta"
                  className="flex-1 border-2 border-gray-300 rounded-lg px-3 py-1.5 uppercase font-bold text-gray-800 focus:border-indigo-500 outline-none" />
                <button onClick={() => checkAna(i)} data-testid={`ana-check-${i}`} className="bg-indigo-600 text-white px-3 rounded-lg font-bold">OK</button>
              </div>
              {status[i] === "ok" && <p className="text-green-600 text-sm font-bold mt-1" data-testid={`ana-ok-${i}`}>✅ Esatto!</p>}
              {status[i] === "wrong" && <p className="text-red-600 text-sm font-bold mt-1">Riprova...</p>}
            </div>
          ))}
        </div>
      )}

      {tab === "rebus" && (
        <div className="space-y-4">
          {rebus.map((r, i) => (
            <div key={r.answer} className="bg-gray-50 rounded-xl p-3" data-testid={`reb-${i}`}>
              <div className="text-xl font-bold text-gray-800 mb-1">{r.clue}</div>
              <p className="text-xs text-gray-500 mb-2">Indizio: {r.hint}</p>
              {revealed[i] ? (
                <p className="text-green-600 font-bold" data-testid={`reb-sol-${i}`}>Soluzione: {r.answer}</p>
              ) : (
                <button onClick={() => setRevealed((s) => ({ ...s, [i]: true }))} data-testid={`reb-reveal-${i}`}
                  className="bg-amber-500 text-white px-3 py-1.5 rounded-lg font-bold text-sm">Mostra soluzione</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== HUB =====================
const GAMES = [
  { key: "cruciverba", icon: "✏️", title: "Parole Crociate", desc: "Definizioni e caselle da riempire" },
  { key: "crucipuzzle", icon: "🔎", title: "Crucipuzzle", desc: "Trova le parole nascoste" },
  { key: "sudoku", icon: "🔢", title: "Sudoku", desc: "La sfida dei numeri" },
  { key: "rebus", icon: "🧠", title: "Rebus & Anagrammi", desc: "Ingegno visivo e verbale" },
];

export default function SettimanaEnigmistica({ onBack, apiBase, onSolved }) {
  const [sub, setSub] = useState(null);
  const [data, setData] = useState({ cruciverba: CRUCIVERBA, anagrammi: ANAGRAMMI, rebus: REBUS, crucipuzzle: CP_WORDS });
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [genKey, setGenKey] = useState(0);
  const [preloaded, setPreloaded] = useState(false);
  const nextRef = useRef(null);

  const fetchSet = useCallback(() => {
    return fetch(`${apiBase}/enigmi/generate`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
  }, [apiBase]);

  // Precarica in background il prossimo set così "Nuovi enigmi" è istantaneo
  const prefetch = useCallback(() => {
    if (!apiBase || nextRef.current) return;
    const p = fetchSet();
    nextRef.current = p;
    p.then((d) => { if (nextRef.current === p && d) setPreloaded(true); });
  }, [apiBase, fetchSet]);

  useEffect(() => { prefetch(); }, [prefetch]);

  const generate = async () => {
    if (!apiBase) return;
    setGenerating(true);
    setGenError("");
    const pending = nextRef.current || fetchSet();
    nextRef.current = null;
    setPreloaded(false);
    const d = await pending;
    if (d) {
      setData({
        cruciverba: d.cruciverba,
        anagrammi: d.anagrammi,
        rebus: d.rebus,
        crucipuzzle: d.crucipuzzle && d.crucipuzzle.length >= 4 ? d.crucipuzzle : CP_WORDS,
      });
      setGenKey((k) => k + 1);
    } else {
      setGenError("Non è stato possibile generare nuovi enigmi, riprova.");
    }
    setGenerating(false);
    prefetch();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-700 via-stone-600 to-neutral-700 p-4" data-testid="enigmistica-screen">
      <div className="max-w-xl mx-auto">
        <button
          onClick={() => (sub ? setSub(null) : onBack())}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 transition-colors mb-6"
          data-testid="enig-back-btn"
        >
          ← {sub ? "Enigmi" : "Torna alla Home"}
        </button>

        {!sub && (
          <>
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white">🧩 La Settimana Enigmistica</h2>
              <p className="text-white/70 mt-1">Enigmi classici per i grandi</p>
            </div>

            <button
              onClick={generate}
              disabled={generating}
              className="w-full mb-4 bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white py-3 rounded-2xl font-bold shadow-lg hover:opacity-90 disabled:opacity-60"
              data-testid="enig-generate-btn"
            >
              {generating ? "✨ Generazione in corso..." : preloaded ? "⚡ Nuovi enigmi con l'AI (pronti!)" : "✨ Nuovi enigmi con l'AI"}
            </button>
            {preloaded && !generating && <p className="text-emerald-200 text-xs text-center -mt-2 mb-3" data-testid="enig-prefetch-ready">Prossimi enigmi già pronti: appariranno all'istante</p>}
            {genError && <p className="text-red-200 text-sm text-center mb-3" data-testid="enig-gen-error">{genError}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {GAMES.map((g) => (
                <button
                  key={g.key}
                  onClick={() => setSub(g.key)}
                  data-testid={`enig-open-${g.key}`}
                  className="bg-white rounded-2xl p-5 shadow-lg hover:scale-[1.02] transition-transform text-left flex items-center gap-4"
                >
                  <span className="text-4xl">{g.icon}</span>
                  <span>
                    <span className="block font-bold text-gray-800">{g.title}</span>
                    <span className="block text-sm text-gray-500">{g.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {sub === "sudoku" && <Sudoku onSolved={onSolved} />}
        {sub === "crucipuzzle" && <Crucipuzzle key={genKey} words={data.crucipuzzle} onSolved={onSolved} />}
        {sub === "cruciverba" && <Cruciverba key={genKey} items={data.cruciverba} onSolved={onSolved} />}
        {sub === "rebus" && <RebusAnagrammi key={genKey} anagrammi={data.anagrammi} rebus={data.rebus} onSolved={onSolved} />}
      </div>
    </div>
  );
}
